// ============================================================================
// CrewAhead Maritime Platform - Profile API Functions
// ============================================================================

import { apiClient, handleApiError } from './client';
import { API_ENDPOINTS } from '../utils/constants';
import type {
  Certification,
  CertificationCreateData,
  CompanyProfile,
  Document,
  GetProfileResponse,
  SeaServiceCreateData,
  SeaServiceRecord,
  SeafarerProfile,
  ShoreProfile,
  UpdateProfileResponse,
  User,
} from '../types';

// ─── Profile CRUD ────────────────────────────────────────────────────────────

/**
 * Fetches the current user's profile (user + role-specific profile).
 *
 * @returns Combined user and profile data
 */
export async function getProfile(): Promise<{
  user: User;
  profile: SeafarerProfile | ShoreProfile | CompanyProfile;
}> {
  try {
    const response =
      await apiClient.get<GetProfileResponse>(API_ENDPOINTS.PROFILE.DETAIL);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Updates the current user's profile.
 *
 * @param data - Partial profile data to update
 * @returns Updated user and profile data
 */
export async function updateProfile(
  data: Partial<SeafarerProfile> | Partial<ShoreProfile> | Partial<CompanyProfile>
): Promise<{
  user: User;
  profile: SeafarerProfile | ShoreProfile | CompanyProfile;
}> {
  try {
    const response = await apiClient.put<UpdateProfileResponse>(
      API_ENDPOINTS.PROFILE.UPDATE,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Document Upload ─────────────────────────────────────────────────────────

/**
 * Uploads a document file to the user's profile.
 *
 * @param file - The file to upload
 * @param type - Document type classification
 * @param metadata - Additional document metadata
 * @returns The uploaded document record
 */
export async function uploadDocument(
  file: File,
  type: string,
  metadata?: {
    documentNumber?: string;
    issueDate?: string;
    expiryDate?: string;
    issuingAuthority?: string;
  }
): Promise<Document> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    if (metadata?.documentNumber) {
      formData.append('documentNumber', metadata.documentNumber);
    }
    if (metadata?.issueDate) {
      formData.append('issueDate', metadata.issueDate);
    }
    if (metadata?.expiryDate) {
      formData.append('expiryDate', metadata.expiryDate);
    }
    if (metadata?.issuingAuthority) {
      formData.append('issuingAuthority', metadata.issuingAuthority);
    }

    const response = await apiClient.post<Document>(
      API_ENDPOINTS.PROFILE.UPLOAD_DOCUMENT,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Deletes a document from the user's profile.
 *
 * @param documentId - ID of the document to delete
 */
export async function deleteDocument(documentId: string): Promise<void> {
  try {
    await apiClient.delete<void>(
      API_ENDPOINTS.PROFILE.DELETE_DOCUMENT(documentId)
    );
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Fetches all documents for the current user.
 *
 * @returns Array of documents
 */
export async function getDocuments(): Promise<Document[]> {
  try {
    const response = await apiClient.get<Document[]>(
      API_ENDPOINTS.PROFILE.UPLOAD_DOCUMENT
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Sea Service ─────────────────────────────────────────────────────────────

/**
 * Fetches all sea service records for the current user.
 *
 * @returns Array of sea service records
 */
export async function getSeaService(): Promise<SeaServiceRecord[]> {
  try {
    const response = await apiClient.get<SeaServiceRecord[]>(
      API_ENDPOINTS.PROFILE.SEA_SERVICE
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Creates a new sea service record.
 *
 * @param data - Sea service record data
 * @returns The created sea service record
 */
export async function createSeaService(
  data: SeaServiceCreateData
): Promise<SeaServiceRecord> {
  try {
    const response = await apiClient.post<SeaServiceRecord>(
      API_ENDPOINTS.PROFILE.SEA_SERVICE,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Updates an existing sea service record.
 *
 * @param id - Sea service record ID
 * @param data - Updated sea service data
 * @returns The updated sea service record
 */
export async function updateSeaService(
  id: string,
  data: Partial<SeaServiceCreateData>
): Promise<SeaServiceRecord> {
  try {
    const response = await apiClient.patch<SeaServiceRecord>(
      API_ENDPOINTS.PROFILE.SEA_SERVICE_DETAIL(id),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Deletes a sea service record.
 *
 * @param id - Sea service record ID
 */
export async function deleteSeaService(id: string): Promise<void> {
  try {
    await apiClient.delete<void>(
      API_ENDPOINTS.PROFILE.SEA_SERVICE_DETAIL(id)
    );
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Certifications ──────────────────────────────────────────────────────────

/**
 * Fetches all certifications for the current user.
 *
 * @returns Array of certifications
 */
export async function getCertifications(): Promise<Certification[]> {
  try {
    const response = await apiClient.get<Certification[]>(
      API_ENDPOINTS.PROFILE.CERTIFICATIONS
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Creates a new certification record.
 *
 * @param data - Certification data
 * @returns The created certification
 */
export async function createCertification(
  data: CertificationCreateData
): Promise<Certification> {
  try {
    const response = await apiClient.post<Certification>(
      API_ENDPOINTS.PROFILE.CERTIFICATIONS,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Updates an existing certification.
 *
 * @param id - Certification ID
 * @param data - Updated certification data
 * @returns The updated certification
 */
export async function updateCertification(
  id: string,
  data: Partial<CertificationCreateData>
): Promise<Certification> {
  try {
    const response = await apiClient.patch<Certification>(
      API_ENDPOINTS.PROFILE.CERTIFICATION_DETAIL(id),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Deletes a certification.
 *
 * @param id - Certification ID
 */
export async function deleteCertification(id: string): Promise<void> {
  try {
    await apiClient.delete<void>(
      API_ENDPOINTS.PROFILE.CERTIFICATION_DETAIL(id)
    );
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Uploads a certificate document file and creates the certification record.
 *
 * @param certificationData - Certification details
 * @param file - Certificate document file (optional)
 * @returns The created certification with document
 */
export async function uploadCertificationWithFile(
  certificationData: CertificationCreateData,
  file?: File
): Promise<Certification> {
  try {
    const formData = new FormData();

    // Append all certification fields
    Object.entries(certificationData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });

    if (file) {
      formData.append('document_file', file);
    }

    const response = await apiClient.post<Certification>(
      `${API_ENDPOINTS.PROFILE.CERTIFICATIONS}/upload`,
      formData
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
